import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { t } from "../constants/i18n";
import type { Theme } from "../constants/themes";
import type { Lang } from "../constants/i18n";
import { DOMAINS, LAB_HOST, SITE } from "../constants/domains";
import "./Nav.css";

interface NavProps {
  theme: Theme;
  changeTheme: (th: Theme) => void;
  lang: Lang;
  changeLang: (l: Lang) => void;
}

const TABS: { path: string; key: Parameters<typeof t>[1] }[] = [
  { path: "/", key: "nav_home" },
  { path: "/projects", key: "nav_projects" },
  { path: "/products", key: "nav_products" },
  { path: "/about", key: "nav_about" },
];

export default function Nav({ theme, changeTheme, lang, changeLang }: NavProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const selects = (
    <>
      <div className="select-wrap">
        <span className="select-label">{t(lang, "select_lang")}</span>
        <select
          className="nav-select"
          value={lang}
          onChange={(e) => changeLang(e.target.value as Lang)}
          aria-label="language"
        >
          <option value="en">{t(lang, "language_english")}</option>
          <option value="jp">{t(lang, "language_japanese")}</option>
        </select>
      </div>
      <div className="select-wrap">
        <span className="select-label">{t(lang, "select_theme")}</span>
        <select
          className="nav-select"
          value={theme}
          onChange={(e) => changeTheme(e.target.value as Theme)}
          aria-label="theme"
        >
          <option value="white">{t(lang, "theme_white")}</option>
          <option value="cream">{t(lang, "theme_cream")}</option>
          <option value="dark">{t(lang, "theme_dark")}</option>
          <option value="black">{t(lang, "theme_black")}</option>
        </select>
      </div>
    </>
  );

  return (
    <>
      <nav className="nav">
        <div className="nav-container">
          <div className="nav-inner">
            <div className="nav-left">
              <button
                className={`nav-hamburger${menuOpen ? " open" : ""}`}
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="menu"
                aria-expanded={menuOpen}
              >
                <span />
                <span />
                <span />
              </button>
              <div className="nav-sep nav-sep-mobile" />

              <a className="nav-back nav-back-desktop" href={SITE.main} title={DOMAINS.root}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                main
              </a>
              <div className="nav-sep nav-sep-desktop" />
              <Link className="nav-logo-btn" to="/">
                <span>The Lab</span>
                <span className="nav-logo-sub">{LAB_HOST}</span>
              </Link>
            </div>

            <div className="nav-tabs">
              {TABS.map((tab) => (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  end
                  className={({ isActive }) => `nav-tab${isActive ? " active" : ""}`}
                >
                  {t(lang, tab.key)}
                </NavLink>
              ))}
            </div>

            <div className="nav-right">
              <div className="nav-right-desktop">{selects}</div>
            </div>
          </div>
        </div>
      </nav>

      <div className={`nav-mobile-menu${menuOpen ? " open" : ""}`}>
        {TABS.map((tab) => (
          <NavLink
            key={tab.path}
            to={tab.path}
            end
            className={({ isActive }) => `nav-mobile-tab${isActive ? " active" : ""}`}
            onClick={() => setMenuOpen(false)}
          >
            {t(lang, tab.key)}
          </NavLink>
        ))}

        <div className="nav-mobile-controls">
          <a className="nav-mobile-main" href={SITE.main}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            {DOMAINS.root}
          </a>
          <div className="nav-mobile-selects">{selects}</div>
        </div>
      </div>
      {menuOpen && <div className="nav-mobile-backdrop" onClick={() => setMenuOpen(false)} />}
    </>
  );
}
